import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { TRANSLATIONS, RTL_LANGS } from "@/i18n/translations";

const LanguageContext = createContext(null);

function getPath(obj, path) {
  return path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    const fromUrl = new URLSearchParams(window.location.search).get("lang");
    const codes = ["it", "en", "de", "fr", "es", "ar"];
    if (fromUrl && codes.includes(fromUrl)) {
      localStorage.setItem("aura_lang", fromUrl);
      return fromUrl;
    }
    return localStorage.getItem("aura_lang") || "it";
  });

  const applyDir = useCallback((l) => {
    const rtl = RTL_LANGS.includes(l);
    document.documentElement.setAttribute("lang", l);
    document.documentElement.setAttribute("dir", rtl ? "rtl" : "ltr");
  }, []);

  useEffect(() => { applyDir(lang); }, [lang, applyDir]);

  const setLang = useCallback((l) => {
    localStorage.setItem("aura_lang", l);
    setLangState(l);
    // Keep URL in sync: drop any ?lang= param so it doesn't override the manual choice on reload
    const url = new URL(window.location.href);
    if (url.searchParams.has("lang")) {
      url.searchParams.delete("lang");
      window.history.replaceState({}, "", url.pathname + url.search + url.hash);
    }
  }, []);

  const t = useCallback((path) => {
    const dict = TRANSLATIONS[lang] || TRANSLATIONS.it;
    const val = getPath(dict, path);
    if (val !== undefined) return val;
    const fallback = getPath(TRANSLATIONS.it, path);
    return fallback !== undefined ? fallback : path;
  }, [lang]);

  const isRTL = RTL_LANGS.includes(lang);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, isRTL }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLang = () => useContext(LanguageContext);
